import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BuildingReport as ReportData } from '../api/types';
import { BuildingReportView } from '../components/BuildingReport';

const API_BASE = import.meta.env.VITE_API_URL || 'https://billing-api.utilsync.cloud';

declare global {
  // Set once the report + charts have painted; the pdf-generator's headless
  // browser waits on this before snapshotting the page to PDF.
  interface Window { __REPORT_READY__?: boolean }
}

// Chrome-less, auth-free print page. The pdf-generator's Browser Rendering
// loads this via a SIGNED URL, waits for window.__REPORT_READY__, then prints
// it to PDF. Authorization is the HMAC token in the query string (verified by
// billing-api's /print/report-data) — there is no Keycloak session here, which
// is exactly why this route lives outside ProtectedRoute.
export default function PrintBuildingReport() {
  const [params] = useSearchParams();
  const [data, setData] = useState<ReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sid = params.get('sid');
  const exp = params.get('exp');
  const sig = params.get('sig');

  // Defence in depth for C1: this route must always render as a fixed light
  // document (it's snapshotted straight to a customer PDF invoice), so pin
  // it regardless of the visiting browser's theme preference or any stray
  // `dark` class left on <html> (e.g. useTheme's localStorage persistence).
  // BuildingReportView's own explicit `color` is what actually fixes the
  // contrast collapse on both paths — this is a belt-and-braces second
  // layer in case anything else in the tree ever depends on inherited theme
  // state.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.style.colorScheme = 'light';
    // printBackground is on, so the app's page background printed as a grey
    // block under the report's last card (2026-10-15). The page is white.
    document.documentElement.style.background = 'white';
    document.body.style.background = 'white';
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sid || !exp || !sig) { setError('Missing token'); return; }
      try {
        const url = `${API_BASE}/print/report-data?sid=${encodeURIComponent(sid)}&exp=${encodeURIComponent(exp)}&sig=${encodeURIComponent(sig)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`report-data ${res.status}`);
        const json = (await res.json()) as ReportData;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load report');
      }
    })();
    return () => { cancelled = true; };
  }, [sid, exp, sig]);

  // Signal readiness only after the report + charts have painted. Two rAFs let
  // Recharts' ResponsiveContainer measure and draw (chart animation is already
  // disabled in BuildingReportView), and a timeout is a belt-and-braces
  // fallback. On error we deliberately DO NOT set ready, so the headless
  // render times out and the queue retries.
  useEffect(() => {
    if (!data) return;
    let done = false;
    const mark = () => { if (!done) { done = true; window.__REPORT_READY__ = true; } };
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(mark, 150)));
    const fallback = setTimeout(mark, 3000);
    return () => { cancelAnimationFrame(raf); clearTimeout(fallback); };
  }, [data]);

  if (error) {
    return <div style={{ padding: 24, fontFamily: 'system-ui', color: '#b91c1c' }}>Report unavailable: {error}</div>;
  }
  if (!data) {
    return <div style={{ padding: 24, fontFamily: 'system-ui', color: '#6b7280' }}>Loading report…</div>;
  }

  return (
    // `color` added alongside the pre-existing `background: 'white'` — belt
    // and braces alongside BuildingReportView's own explicit `color` (the
    // actual C1 fix) and the light-pin effect above.
    // Exactly A4's printable width: 210 mm less the pdf-generator's 10 mm side
    // margins. The charts measure their width on screen before printing, so
    // the page they are measured on must be the page they print on.
    <div data-testid="print-page" style={{ background: 'white', color: '#111827', width: '190mm', margin: '0 auto' }}>
      <BuildingReportView data={data} layout="print" />
    </div>
  );
}
